import React, { useState, useEffect } from 'react';
import { consolAdjustmentsApi } from '../services/api';
import '../styles/Panel.css';

function ConsolAdjustmentsPanel({ entity }) {
  const [adjustmentDate, setAdjustmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);

  const [reportsList, setReportsList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [pushingToFinalyzerV2, setPushingToFinalyzerV2] = useState(null);

  useEffect(() => {
    if (entity) {
      loadReportsList();
    }
  }, [entity]);

  const loadReportsList = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await consolAdjustmentsApi.listReports(entity.name);
      setReportsList(data.reports || []);
    } catch (err) {
      console.error('Error loading consol adjustments list:', err);
      setListError(err.message);
    } finally {
      setListLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        alert('Please select an Excel file (.xlsx or .xls)');
        event.target.value = null;
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    if (!adjustmentDate) {
      alert('Please select an adjustment date');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const data = await consolAdjustmentsApi.uploadJournalEntries(
        entity.name,
        adjustmentDate,
        selectedFile
      );

      if (data.success) {
        setUploadResult(data);
        setSelectedFile(null);
        document.getElementById('consol-file-input').value = null;
        await loadReportsList();
      } else {
        setError(data.error || 'Upload failed');
        if (data.validation_errors && data.validation_errors.length > 0) {
          setError(
            <div>
              <div>{data.error}</div>
              <ul style={{ marginTop: '10px' }}>
                {data.validation_errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          );
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to upload consol adjustments');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await consolAdjustmentsApi.downloadTemplate();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'journal_entry_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      alert(`Error downloading template: ${err.message}`);
    }
  };

  const handleDownload = async (adjustment_date, timestamp) => {
    setDownloadingFile(`${adjustment_date}_${timestamp}`);
    try {
      const blob = await consolAdjustmentsApi.downloadReport(
        entity.name,
        adjustment_date,
        timestamp
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Consol_Adjustments_${entity.name}_${adjustment_date}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading consol adjustments:', err);
      alert(`Error downloading file: ${err.message}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  const handlePushToFinalyzerV2 = async (adjustment_date, timestamp) => {
    if (!window.confirm(`Push consolidated adjustments for ${adjustment_date} to Finalyzer (V2 with nature_of_report: "Consolidated")?`)) {
      return;
    }

    setPushingToFinalyzerV2(`${adjustment_date}_${timestamp}`);
    try {
      const data = await consolAdjustmentsApi.pushToFinalyzerV2(
        entity.name,
        adjustment_date,
        timestamp
      );

      if (data.success) {
        alert(`Successfully pushed ${data.records_sent} consol adjustment entries to Finalyzer (V2 - Consolidated)`);
      } else {
        alert(`Error pushing to Finalyzer: ${data.error}`);
      }
    } catch (err) {
      console.error('Error pushing consol adjustments to Finalyzer:', err);
      alert(`Error pushing to Finalyzer: ${err.message}`);
    } finally {
      setPushingToFinalyzerV2(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(9, 11);
      const minute = timestamp.substring(11, 13);
      const second = timestamp.substring(13, 15);
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch {
      return timestamp;
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  return (
    <div className="panel consol-adjustments-panel">
      <h2>Consol Adjustments - {entity.name}</h2>

      <div className="alert alert-info" style={{ marginBottom: '20px', backgroundColor: '#e8f5e9', borderColor: '#4caf50', color: '#2e7d32' }}>
        <strong>Note:</strong> Upload consolidated adjustment entries. Same sheet format as Journal Adjustments (GL Account, Narration, Debit Amount, Credit Amount).
        <br />
        <br />
        <strong>Push to Finalyzer:</strong> Uses V2 API with <code>nature_of_report: "Consolidated"</code>.
        <br />
        <br />
        <strong>Process:</strong>
        <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
          <li>Download the template (same as Journal Adjustments)</li>
          <li>Fill in GL Account, Narration, Debit Amount, Credit Amount</li>
          <li>Upload the file</li>
          <li>Push to Finalyzer (V2 - Consolidated) when ready</li>
        </ul>
        <strong>Validation:</strong> Debit and Credit amounts must balance.
      </div>

      <div className="section">
        <h3>Upload Consol Adjustment Sheet</h3>

        <div className="form-group">
          <label>Adjustment Date:</label>
          <input
            type="date"
            value={adjustmentDate}
            onChange={(e) => setAdjustmentDate(e.target.value)}
            disabled={uploading}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>Excel File:</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              id="consol-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              disabled={uploading}
              className="form-control"
            />
            <button
              onClick={handleDownloadTemplate}
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Download Template
            </button>
          </div>
          {selectedFile && (
            <div style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </div>
          )}
        </div>

        <button
          onClick={handleUpload}
          disabled={uploading || !selectedFile || !adjustmentDate}
          className="btn btn-primary"
        >
          {uploading ? 'Uploading...' : 'Upload Consol Adjustments'}
        </button>

        {uploadResult && (
          <div className="alert alert-success" style={{ marginTop: '15px' }}>
            <h4>Upload Successful</h4>
            <p>Entity: {uploadResult.entity_name.toUpperCase()}</p>
            <p>Adjustment Date: {uploadResult.adjustment_date}</p>
            <p>Total Entries: {uploadResult.total_entries}</p>
            <p>Total Debit: {formatNumber(uploadResult.total_debit)}</p>
            <p>Total Credit: {formatNumber(uploadResult.total_credit)}</p>
            <p>Balanced: {uploadResult.balanced ? 'Yes' : 'No'}</p>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              S3 Path: {uploadResult.s3_path}
            </p>
          </div>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginTop: '15px' }}>
            <h4>Upload Error</h4>
            <div>{error}</div>
          </div>
        )}
      </div>

      <div className="section" style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Uploaded Consol Adjustments</h3>
          <button
            onClick={loadReportsList}
            disabled={listLoading}
            className="btn btn-secondary"
          >
            {listLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {listLoading && <p>Loading consol adjustments...</p>}
        {listError && (
          <div className="alert alert-error">
            <p>Error: {listError}</p>
          </div>
        )}

        {!listLoading && reportsList.length === 0 && (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            No consol adjustments uploaded yet for this entity.
          </p>
        )}

        {!listLoading && reportsList.length > 0 && (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Adjustment Date</th>
                  <th>Upload Time</th>
                  <th>Total Entries</th>
                  <th>Total Debit</th>
                  <th>Total Credit</th>
                  <th>Balanced</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportsList.map((report, idx) => (
                  <tr key={idx}>
                    <td>{formatDate(report.adjustment_date)}</td>
                    <td>{formatTimestamp(report.timestamp)}</td>
                    <td>{report.metadata?.total_entries || 'N/A'}</td>
                    <td>{formatNumber(report.metadata?.total_debit)}</td>
                    <td>{formatNumber(report.metadata?.total_credit)}</td>
                    <td>
                      {report.metadata?.balanced ? (
                        <span style={{ color: 'green', fontWeight: 'bold' }}>Yes</span>
                      ) : (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>No</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleDownload(report.adjustment_date, report.timestamp)}
                          disabled={downloadingFile === `${report.adjustment_date}_${report.timestamp}`}
                          className="btn btn-sm btn-secondary"
                        >
                          {downloadingFile === `${report.adjustment_date}_${report.timestamp}`
                            ? 'Downloading...'
                            : 'Download'}
                        </button>
                        <button
                          onClick={() => handlePushToFinalyzerV2(report.adjustment_date, report.timestamp)}
                          disabled={
                            pushingToFinalyzerV2 === `${report.adjustment_date}_${report.timestamp}` ||
                            !report.metadata?.balanced
                          }
                          className="btn btn-sm btn-primary"
                          title={!report.metadata?.balanced ? 'Cannot push unbalanced entries' : 'Push to Finalyzer V2 with nature_of_report: Consolidated'}
                        >
                          {pushingToFinalyzerV2 === `${report.adjustment_date}_${report.timestamp}`
                            ? 'Pushing...'
                            : 'Push to Finalyzer (V2 - Consolidated)'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsolAdjustmentsPanel;
